import type { IndeedIntegrationSettings } from 'api-clients/monolith';
import { FormattedMessage, useIntl } from 'react-intl';

import { useToaster } from '@/components/base/toaster';
import { ChannelIcon } from '@/components/ui/channel-icon';
import { Tag } from '@/components/ui/tag';
import { useIndeedPanelParams } from '@/hooks/use-indeed-panel-params';
import {
  useIndeedSettingsWriting,
  useUpdateIndeedSettings,
} from '@/hooks/use-indeed-settings';
import { INDEED } from '@/types/channels';

import { ActionButton } from './action-button';
import { StatusPill } from './status-pill';

export function IndeedOrganicRow({
  settings,
}: {
  settings: IndeedIntegrationSettings;
}) {
  const intl = useIntl();
  const { open } = useIndeedPanelParams();
  const { notifySuccess, notifyError } = useToaster();
  const update = useUpdateIndeedSettings();
  const isWriting = useIndeedSettingsWriting();
  const isActivated =
    settings.indeed_authorized && settings.indeed_xml_feed_enabled;

  const deactivate = () => {
    if (isWriting) return;
    update.mutate(
      { indeed_xml_feed_enabled: false },
      {
        onSuccess: () =>
          notifySuccess({
            message: intl.formatMessage({
              defaultMessage: 'Channel deactivated successfully',
              description:
                'Success message for successful channel deactivation',
            }),
          }),
        onError: () =>
          notifyError({
            message: intl.formatMessage({
              defaultMessage: 'Failed to deactivate channel',
              description: 'Error message for failed channel deactivation',
            }),
          }),
      },
    );
  };

  return (
    <>
      <div
        className="grid items-center gap-x-4 px-4 py-3"
        style={{ gridTemplateColumns: '1fr 1fr 2fr' }}
        data-testid="indeed-organic-row"
      >
        <div className="flex items-center gap-2">
          <ChannelIcon
            channelSlug={INDEED}
            channelName="Indeed Organic"
            variant="large"
            size="sm"
          />
          <Tag
            text={
              <FormattedMessage
                defaultMessage="Organic"
                description="Unpaid job listings distributed through Indeed's organic feed"
              />
            }
          />
        </div>
        <StatusPill isConnected={isActivated} />
        <div className="flex items-center">
          <ActionButton
            activateChannel={() => open({ organic: true })}
            deactivateChannel={deactivate}
            isActivated={isActivated}
            isActivating={false}
            isDeactivating={isWriting}
            isJoveo={false}
          />
        </div>
      </div>
      <div className="bg-fx-neutral-200 h-px w-full" />
    </>
  );
}
